// What a drag draws to show what it is doing: the axis guide while Ctrl holds
// it to one axis (CO-5850), the alignment guide while a face lines up across
// a gap (CO-5851), and a guide for the snap it took (CO-5858). The axis guide
// has a thin solid outline; the alignment's bridge has none; the faces lined
// up are filled stronger and outlined, drawn over the bridge. These movement
// guides are MOVEMENT_GUIDE_COLOUR. A face snap fills each face it used,
// outlining those snapping, a corner snap marks each corner that met, and a
// product rule outlines the product it snapped to, in SNAP_GUIDE_COLOUR,
// the moving product's corner apart in MOVING_CORNER_COLOUR.
//
// The faces snapped or lined up on products fill in depth, hidden behind
// what stands in front of them, with their outlines drawn over everything,
// so where they are still shows through the products in front; the corner
// markers and a product rule's outline draw over everything too. The rest
// draws in depth, outline and all: the axis guide, the alignment's bridge
// and a wall's highlight.
import * as THREE from 'three';
import {Axis} from 'components/customer/RoomPlanner/helpers/axes';
import {
    axisGuideOutline,
    axisGuidePlanes,
} from 'components/customer/RoomPlanner/helpers/movement/axisGuide';
import {
    alignedFaceOutline,
    alignedFacePlanes,
    alignedWallOutline,
    alignedWallPlanes,
    alignmentBridgeOutline,
    alignmentBridgePlanes,
    alignmentGuide,
    isEdgeOn,
} from 'components/customer/RoomPlanner/helpers/movement/alignmentGuide';
import {
    AlignmentBody,
    HeldFaceAlignment,
} from 'components/customer/RoomPlanner/helpers/movement/faceAlignment';
import {
    CornerMarker,
    cornerMarkers,
    FaceHighlight,
    productOutline,
    SNAPPED_FACE_OPACITY,
    snappedFaces,
} from 'components/customer/RoomPlanner/helpers/movement/snapGuide';
import type {Snap} from 'components/customer/RoomPlanner/helpers/snapping/snaps';
import type {SnapPlane} from 'components/customer/RoomPlanner/helpers/snapping/strategies/faceSnap';

// The colour of the guides for moving: held to an axis, and lined up.
export const MOVEMENT_GUIDE_COLOUR = 0x1e88e5;
// The colour of the guides for a snap: the green of a snapped product's
// outline (Preview3D/helpers/Three.ts), so the two read as one.
export const SNAP_GUIDE_COLOUR = 0x4caf50;
// The moving product's corner in a corner snap, apart from the other's.
export const MOVING_CORNER_COLOUR = 0xf59e0b;

// The walls and floor are transparent, so they draw in the transparent pass
// after every opaque object. Only a transparent guide drawn last there stays
// on top.
const ON_TOP = {depthTest: false, depthWrite: false, transparent: true};
// A guide in depth is still transparent and writes no depth, so the guides
// drawn after it, and each other, still show through it.
const DEPTH = {depthTest: true, depthWrite: false, transparent: true};

// The guides drawn as planes and an outline, kept and reshaped every move.
type GuideName =
    | 'axisGuide'
    | 'alignmentBridge'
    | 'alignedWalls'
    | 'alignedFaces'
    | 'snapProduct';

// The snap guides built of meshes, as one group each.
type GroupName = 'snapWalls' | 'snapFaces' | 'snapCorners';

/**
 * How a guide draws.
 */
interface GuideStyle<
    Name extends GuideName | GroupName = GuideName | GroupName,
> {
    // What it and its parts are named: its key in GUIDES.
    name: Name;
    // When it draws among the guides, its outline one after its fill.
    renderOrder: number;
    // Its colour; the corner markers' own corner apart.
    colour: number;
    // Whether its fill and its outline draw over everything (ON_TOP) or in
    // depth (DEPTH); no fill for a guide that is only an outline.
    fill?: typeof ON_TOP;
    outline: typeof ON_TOP;
}

// Every guide, in the order they draw, last of all and so over everything
// else: the alignment over the axis guide, so where a held product will line
// up reads clearly against its corridor, and the faces lined up over the
// bridge between them; a snap's guide over those.
//
// The axis guide, the bridge and a wall's highlight draw in depth, hidden
// behind what stands in front of them. The faces snapped or lined up on
// products fill in depth with their outlines over everything, so where they
// are still shows through the products in front. The corner markers and a
// product rule's outline draw over everything.
const GUIDES = {
    axisGuide: {
        name: 'axisGuide',
        renderOrder: 999,
        colour: MOVEMENT_GUIDE_COLOUR,
        fill: DEPTH,
        outline: DEPTH,
    },
    alignmentBridge: {
        name: 'alignmentBridge',
        renderOrder: 1001,
        colour: MOVEMENT_GUIDE_COLOUR,
        fill: DEPTH,
        outline: DEPTH,
    },
    alignedWalls: {
        name: 'alignedWalls',
        renderOrder: 1003,
        colour: MOVEMENT_GUIDE_COLOUR,
        fill: DEPTH,
        outline: DEPTH,
    },
    alignedFaces: {
        name: 'alignedFaces',
        renderOrder: 1005,
        colour: MOVEMENT_GUIDE_COLOUR,
        fill: DEPTH,
        outline: ON_TOP,
    },
    snapWalls: {
        name: 'snapWalls',
        renderOrder: 1007,
        colour: SNAP_GUIDE_COLOUR,
        fill: DEPTH,
        outline: DEPTH,
    },
    snapFaces: {
        name: 'snapFaces',
        renderOrder: 1009,
        colour: SNAP_GUIDE_COLOUR,
        fill: DEPTH,
        outline: ON_TOP,
    },
    snapProduct: {
        name: 'snapProduct',
        renderOrder: 1011,
        colour: SNAP_GUIDE_COLOUR,
        outline: ON_TOP,
    },
    snapCorners: {
        name: 'snapCorners',
        renderOrder: 1013,
        colour: SNAP_GUIDE_COLOUR,
        fill: ON_TOP,
        outline: ON_TOP,
    },
} satisfies {[Name in GuideName | GroupName]: GuideStyle<Name>};

// How far the guides are moved towards the camera (2.5 mm): one in depth
// lies on a wall, the floor or a product's face, and would flicker against
// it.
const TOWARDS_CAMERA = 0.5;

// The shapes the mesh guides share: a diamond on the other product's corner,
// a ring around the moving product's, and a unit box stretched into each
// corner stub.
const DIAMOND = new THREE.OctahedronGeometry(3);
const RING = new THREE.TorusGeometry(5, 0.7, 8, 32);
const UNIT_BOX = new THREE.BoxGeometry(1, 1, 1);
const SHARED = new Set<THREE.BufferGeometry>([DIAMOND, RING, UNIT_BOX]);
const STUB_THICKNESS = 0.9;

/**
 * One guide's planes, when it has any, and outline.
 */
interface Guide {
    planes: THREE.Mesh | null;
    outline: THREE.LineSegments;
}

/**
 * What a move shows.
 */
export interface DragGuideState {
    // The axis the move is held to, if any.
    axis: Axis | null;
    // The dragged product.
    product: THREE.Object3D;
    // The floor's and the visible walls' boxes, where the axis guide stops.
    surfaces: THREE.Box3[];
    // Which way the camera looks, and how it is turned, so a corner's ring
    // faces it.
    viewDirection: THREE.Vector3;
    cameraQuaternion: THREE.Quaternion;
    // The face alignment it lined up with, and the dragged product's solid
    // box and faces where it landed.
    alignment: {held: HeldFaceAlignment; body: AlignmentBody} | null;
    // The snap it took, and the dragged product where it landed.
    snap: {snap: Snap; body: AlignmentBody} | null;
    // Every other product's faces in a plane (PlaneFaces), so the line-up
    // guide shows the faces in line with those it lines up; null to show
    // only the two products'.
    roomProductFaces: ((plane: SnapPlane) => THREE.Box3[]) | null;
    // The shown walls' faces in a plane, likewise, drawn in depth.
    roomWallFaces?: ((plane: SnapPlane) => THREE.Box3[]) | null;
    // The top bar's Guides switch (CO-5856): off, nothing is drawn. Which
    // guides show otherwise follows the snapping choices, since a kind of
    // snap turned off never happens.
    visible: boolean;
}

/**
 * @param {GuideStyle} style Which guide.
 * @return {Guide} An empty guide, its parts named after it.
 */
const createGuide = ({
    name,
    renderOrder,
    colour,
    fill,
    outline: drawn,
}: GuideStyle<GuideName>): Guide => {
    const planes = fill
        ? new THREE.Mesh(
              new THREE.BufferGeometry(),
              new THREE.MeshBasicMaterial({
                  // Each vertex carries its own opacity, so planes can fade.
                  vertexColors: true,
                  side: THREE.DoubleSide,
                  ...fill,
              })
          )
        : null;
    const outline = new THREE.LineSegments(
        new THREE.BufferGeometry(),
        new THREE.LineBasicMaterial({color: colour, ...drawn})
    );
    if (planes) {
        planes.name = `${name}:planes`;
        planes.renderOrder = renderOrder;
    }
    outline.name = `${name}:outline`;
    outline.renderOrder = renderOrder + 1;

    return {planes, outline};
};

/**
 * Replaces a guide's shapes.
 * @param {Guide} guide The guide.
 * @param {GuideStyle} style Which guide, for its colour.
 * @param {object} planes Its planes as triangles, with each vertex's opacity.
 * @param {Array<THREE.Vector3[]>} outline Its outline, as pairs of ends.
 */
const reshape = (
    guide: Guide,
    {colour}: GuideStyle<GuideName>,
    planes: {positions: number[]; alphas: number[]},
    outline: THREE.Vector3[][]
) => {
    if (guide.planes) {
        const {r, g, b} = new THREE.Color(colour);
        const fill = new THREE.BufferGeometry();
        fill.setAttribute(
            'position',
            new THREE.Float32BufferAttribute(planes.positions, 3)
        );
        fill.setAttribute(
            'color',
            new THREE.Float32BufferAttribute(
                planes.alphas.flatMap((alpha) => [r, g, b, alpha]),
                4
            )
        );
        guide.planes.geometry.dispose();
        guide.planes.geometry = fill;
    }

    guide.outline.geometry.dispose();
    guide.outline.geometry = new THREE.BufferGeometry().setFromPoints(
        outline.flat()
    );
};

/**
 * @param {THREE.Vector3} from One end.
 * @param {THREE.Vector3} to The other.
 * @param {number} thickness How thick it is across.
 * @param {THREE.Material} material What it is drawn in.
 * @return {THREE.Mesh} A thick line from one to the other, a stretched box.
 */
const bar = (
    from: THREE.Vector3,
    to: THREE.Vector3,
    thickness: number,
    material: THREE.Material
) => {
    const mesh = new THREE.Mesh(UNIT_BOX, material);
    mesh.position.copy(from).add(to).multiplyScalar(0.5);
    mesh.scale.set(
        Math.max(Math.abs(to.x - from.x), thickness),
        Math.max(Math.abs(to.y - from.y), thickness),
        Math.max(Math.abs(to.z - from.z), thickness)
    );

    return mesh;
};

/**
 * The faces a face snap used, as one group: each filled, and one outline
 * round the faces snapping.
 * @param {FaceHighlight} faces Each face's four corners, and the outline's
 * lines.
 * @param {GuideStyle} style Which guide, the products' faces or the walls'.
 * @return {THREE.Group} The fills and the outline.
 */
const faceGroup = (
    {fills, outline}: FaceHighlight,
    style: GuideStyle<'snapFaces' | 'snapWalls'>
): THREE.Group => {
    const group = new THREE.Group();
    fills.forEach((corners) => {
        const shape = new THREE.BufferGeometry().setFromPoints(corners);
        shape.setIndex([0, 1, 2, 0, 2, 3]);
        const fill = new THREE.Mesh(
            shape,
            new THREE.MeshBasicMaterial({
                color: style.colour,
                ...style.fill,
                opacity: SNAPPED_FACE_OPACITY,
                side: THREE.DoubleSide,
            })
        );
        fill.renderOrder = style.renderOrder;
        group.add(fill);
    });

    // A thin line, as every other guide is outlined.
    const lines = new THREE.LineSegments(
        new THREE.BufferGeometry().setFromPoints(outline.flat()),
        new THREE.LineBasicMaterial({color: style.colour, ...style.outline})
    );
    lines.renderOrder = style.renderOrder + 1;
    group.add(lines);

    return group;
};

/**
 * The markers for two corners that met, as one group.
 * @param {CornerMarker[]} markers The markers.
 * @param {THREE.Quaternion} facing How the camera is turned.
 * @return {THREE.Group} The diamond, ring and stubs.
 */
const cornerGroup = (
    markers: CornerMarker[],
    facing: THREE.Quaternion
): THREE.Group => {
    const group = new THREE.Group();
    markers.forEach(({kind, at, stubs}) => {
        const moving = kind === 'moving';
        const material = () =>
            new THREE.MeshBasicMaterial({
                color: moving
                    ? MOVING_CORNER_COLOUR
                    : GUIDES.snapCorners.colour,
                ...GUIDES.snapCorners.fill,
            });
        const marker = new THREE.Mesh(moving ? RING : DIAMOND, material());
        marker.position.copy(at);
        if (moving) marker.quaternion.copy(facing);
        group.add(marker);

        stubs.forEach((stub) =>
            group.add(bar(at, at.clone().add(stub), STUB_THICKNESS, material()))
        );
    });
    group.children.forEach((part) => {
        part.renderOrder = GUIDES.snapCorners.renderOrder;
    });

    return group;
};

/**
 * @param {THREE.Mesh | THREE.LineSegments} part A guide's mesh or line.
 */
const disposePart = (part: THREE.Mesh | THREE.LineSegments) => {
    if (!SHARED.has(part.geometry)) part.geometry.dispose();
    (part.material as THREE.Material).dispose();
};

/**
 * The guides one drag draws, rebuilt every move and taken away at the end.
 */
export class DragGuides {
    private readonly guides = new Map<GuideName, Guide>();
    private readonly groups = new Map<GroupName, THREE.Group>();

    /**
     * Shows what a move is doing, taking away what it no longer does.
     * @param {THREE.Scene} scene The scene to draw in.
     * @param {DragGuideState} state What the move shows.
     */
    update(scene: THREE.Scene, state: DragGuideState) {
        if (!state.visible) {
            this.clear(scene);
            return;
        }

        if (state.axis === null) {
            this.drop(scene, GUIDES.axisGuide);
        } else {
            const bbox = new THREE.Box3().setFromObject(state.product);
            reshape(
                this.guide(scene, GUIDES.axisGuide),
                GUIDES.axisGuide,
                axisGuidePlanes(bbox, state.axis, state.surfaces),
                axisGuideOutline(bbox, state.axis, state.surfaces)
            );
        }

        if (state.alignment === null) {
            this.drop(scene, GUIDES.alignmentBridge);
            this.drop(scene, GUIDES.alignedWalls);
            this.drop(scene, GUIDES.alignedFaces);
        } else {
            const guide = alignmentGuide(
                state.alignment.held,
                state.alignment.body,
                state.roomProductFaces ?? undefined,
                state.roomWallFaces ?? undefined
            );
            reshape(
                this.guide(scene, GUIDES.alignmentBridge),
                GUIDES.alignmentBridge,
                alignmentBridgePlanes(guide),
                // Unoutlined, so only the faces lined up are outlined,
                // except seen edge-on, in plan or elevation, where its fill
                // vanishes and its outline is all that shows of it.
                isEdgeOn(state.alignment.held.axis, state.viewDirection)
                    ? alignmentBridgeOutline(guide)
                    : []
            );
            if (guide.wallFaces.length === 0) {
                this.drop(scene, GUIDES.alignedWalls);
            } else {
                reshape(
                    this.guide(scene, GUIDES.alignedWalls),
                    GUIDES.alignedWalls,
                    alignedWallPlanes(guide),
                    alignedWallOutline(guide)
                );
            }
            reshape(
                this.guide(scene, GUIDES.alignedFaces),
                GUIDES.alignedFaces,
                alignedFacePlanes(guide),
                alignedFaceOutline(guide)
            );
        }

        this.showSnap(scene, state);
        this.towardsCamera(state.viewDirection);
    }

    /**
     * Takes every guide away.
     * @param {THREE.Scene} scene The scene they are in.
     */
    clear(scene: THREE.Scene) {
        Array.from(this.guides.keys()).forEach((name) =>
            this.drop(scene, {name})
        );
        Array.from(this.groups.keys()).forEach((name) =>
            this.dropGroup(scene, {name})
        );
    }

    /**
     * Shows the guide for the snap a move took, and takes away the others:
     * a face snap's faces, a corner snap's markers with no faces, or the
     * outline of the product a product rule snapped to.
     * @param {THREE.Scene} scene The scene to draw in.
     * @param {DragGuideState} state What the move shows.
     */
    private showSnap(
        scene: THREE.Scene,
        {snap, cameraQuaternion}: DragGuideState
    ) {
        const taken = snap?.snap;
        const other = taken?.other;

        this.dropGroup(scene, GUIDES.snapFaces);
        this.dropGroup(scene, GUIDES.snapWalls);
        if (taken?.face && other && snap) {
            const planes = [taken.face, ...(taken.aligned ?? [])];
            const {walls, ...products} = snappedFaces(planes, other, snap.body);
            if (products.fills.length > 0) {
                this.addGroup(
                    scene,
                    GUIDES.snapFaces,
                    faceGroup(products, GUIDES.snapFaces)
                );
            }
            if (walls.fills.length > 0) {
                this.addGroup(
                    scene,
                    GUIDES.snapWalls,
                    faceGroup(walls, GUIDES.snapWalls)
                );
            }
        }

        this.dropGroup(scene, GUIDES.snapCorners);
        if (taken?.held && other && snap) {
            this.addGroup(
                scene,
                GUIDES.snapCorners,
                cornerGroup(
                    cornerMarkers(taken.held, snap.body.bbox, other),
                    cameraQuaternion
                )
            );
        }

        if (taken?.rule && other) {
            reshape(
                this.guide(scene, GUIDES.snapProduct),
                GUIDES.snapProduct,
                {positions: [], alphas: []},
                productOutline(other.bbox)
            );
        } else {
            this.drop(scene, GUIDES.snapProduct);
        }
    }

    /**
     * Moves the guides a hair towards the camera, off the surface each lies
     * on, so those drawn in depth don't flicker against it.
     * @param {THREE.Vector3} viewDirection Which way the camera looks.
     */
    private towardsCamera(viewDirection: THREE.Vector3) {
        const offset = viewDirection
            .clone()
            .normalize()
            .multiplyScalar(-TOWARDS_CAMERA);
        this.guides.forEach((guide) => {
            guide.planes?.position.copy(offset);
            guide.outline.position.copy(offset);
        });
        this.groups.forEach((group) => group.position.copy(offset));
    }

    /**
     * @param {THREE.Scene} scene The scene to draw in.
     * @param {GuideStyle} style Which guide.
     * @return {Guide} The guide, added to the scene the first time.
     */
    private guide(scene: THREE.Scene, style: GuideStyle<GuideName>): Guide {
        const {name} = style;
        let guide = this.guides.get(name);
        if (!guide) {
            guide = createGuide(style);
            if (guide.planes) scene.add(guide.planes);
            scene.add(guide.outline);
            this.guides.set(name, guide);
        }

        return guide;
    }

    /**
     * @param {THREE.Scene} scene The scene it is in.
     * @param {object} guide The guide to take away, by name.
     */
    private drop(
        scene: THREE.Scene,
        {name}: Pick<GuideStyle<GuideName>, 'name'>
    ) {
        const guide = this.guides.get(name);
        if (!guide) return;

        [guide.planes, guide.outline].forEach((part) => {
            if (!part) return;
            scene.remove(part);
            disposePart(part);
        });
        this.guides.delete(name);
    }

    /**
     * @param {THREE.Scene} scene The scene to draw in.
     * @param {GuideStyle} style Which guide.
     * @param {THREE.Group} group Its meshes.
     */
    private addGroup(
        scene: THREE.Scene,
        {name}: GuideStyle<GroupName>,
        group: THREE.Group
    ) {
        group.name = name;
        scene.add(group);
        this.groups.set(name, group);
    }

    /**
     * Takes a mesh guide away, keeping the shapes its meshes share.
     * @param {THREE.Scene} scene The scene it is in.
     * @param {object} guide The guide to take away, by name.
     */
    private dropGroup(
        scene: THREE.Scene,
        {name}: Pick<GuideStyle<GroupName>, 'name'>
    ) {
        const group = this.groups.get(name);
        if (!group) return;

        scene.remove(group);
        group.children.forEach((part) =>
            disposePart(part as THREE.Mesh | THREE.LineSegments)
        );
        this.groups.delete(name);
    }
}
